import React, {useEffect, useState} from 'react';
import {RouterProvider} from 'react-router-dom';
import {ConfigProvider, Spin} from 'antd';
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {useModuleRegistry} from './core/module-registry';
import {createAppRouter} from './routes';
import './index.css';

import {ThemeProvider} from "@/components/common/ThemeProvider.tsx";
import { AuthProvider } from '@/modules/auth/context/AuthContext';
import { AuthModule } from '@/modules/auth';
import { LocationModule } from './modules/location';
import {InternalAccountModule} from "@/modules/internal-account";
import ReportsModule from "@/modules/reports";
import { ReportsManagementModule } from "@/modules/reports-management";
import {CustomerOnboardingModule} from "@/modules/customer-onboarding";
import {CustomerManagementModule} from "@/modules/customer-management";
import {TransactionMenuModule} from "@/modules/transaction-menu";
import {TransactionHubModule} from "@/modules/transaction-hub";
import {NubUserModule} from "@/modules/nub-user";
import { ActionFlowModule } from '@/modules/action-flow';
import { DayEndModule } from '@/modules/day-end';
import { LoanModule } from '@/modules/loans';
import { LoanDisbursementModule } from '@/modules/loan-disbursement';
import { LoanRepaymentModule } from '@/modules/loan-repayment';
import { CollateralManagementModule } from '@/modules/collateral-management';
import { CoaModule } from '@/modules/coa';
import { CustomerCategoryModule } from '@/modules/customer-category';
import { DocumentTypeModule } from '@/modules/document-type';
import { LoanProductSettingsModule } from '@/modules/loan-product-settings';
import { AccountSettingsModule } from '@/modules/account-settings';
import { CustomerNotificationModule } from '@/modules/customer-notification';
import { NotificationConfigModule } from '@/modules/notification-config';
import { SmsModule } from '@/modules/sms';
import { TemplateModule } from '@/modules/template';
import { TermsAndConditionsModule } from '@/modules/termsandconditions';
import { DraftsModule } from '@/modules/drafts';
import { SavingsProductSettingsModule } from '@/modules/savings-product-settings';
import { SystemConfigModule } from '@/modules/system-config';
import { BranchManagementModule } from '@/modules/branch-management';
import { InAppNotificationModule } from '@/modules/in-app-notifications';
import { InvoiceProductsModule } from '@/modules/invoice-products';
import { InvoiceConfigModule } from '@/modules/invoice-config';
import { InvoicesModule } from '@/modules/invoices';
import { CrmModule } from '@/modules/crm';
import { BatchTransactionModule } from '@/modules/batch-transactions';
import { HrModule } from '@/modules/hr';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 1,
            refetchOnWindowFocus: false,
        },
    },
});

const App: React.FC = () => {
    const {registerModule, getAllRoutes} = useModuleRegistry();
    const [isModulesReady, setModulesReady] = useState(false);

    useEffect(() => {
        // Register all available modules
        registerModule(AuthModule);
        registerModule(LocationModule);
        registerModule(InternalAccountModule);
        // Customer
        registerModule(CustomerOnboardingModule);
        registerModule(CustomerManagementModule);
        registerModule(CustomerCategoryModule);
        registerModule(CustomerNotificationModule);
        // Loans
        registerModule(LoanModule);
        registerModule(LoanDisbursementModule);
        registerModule(LoanRepaymentModule);
        registerModule(LoanProductSettingsModule);
        registerModule(CollateralManagementModule);
        // Transactions
        registerModule(TransactionMenuModule);
        registerModule(TransactionHubModule);
        registerModule(BatchTransactionModule);
        // Invoices
        registerModule(InvoiceProductsModule);
        registerModule(InvoiceConfigModule);
        registerModule(InvoicesModule);
        // CRM
        registerModule(CrmModule);
        // Reports
        registerModule(ReportsModule);
        registerModule(ReportsManagementModule);
        // Admin
        registerModule(CoaModule);
        registerModule(DocumentTypeModule);
        registerModule(AccountSettingsModule);
        registerModule(NotificationConfigModule);
        registerModule(SmsModule);
        registerModule(TemplateModule);
        registerModule(TermsAndConditionsModule);
        registerModule(NubUserModule);
        registerModule(ActionFlowModule);
        registerModule(DayEndModule);
        registerModule(SavingsProductSettingsModule);
        registerModule(SystemConfigModule);
        registerModule(BranchManagementModule);
        registerModule(InAppNotificationModule);

        // HR
        registerModule(HrModule);
        // Support
        registerModule(DraftsModule);

        setModulesReady(true);
    }, [registerModule]);

    // Only create router after modules are registered
    if (!isModulesReady) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Spin size="large"/>
            </div>
        );
    }

    const router = createAppRouter(getAllRoutes());

    return (
        <QueryClientProvider client={queryClient}>
            <ThemeProvider>
                <ConfigProvider
                    theme={{
                        token: {
                            colorPrimary: '#1890ff',
                            borderRadius: 6,
                        },
                    }}
                >
                    <AuthProvider>
                        <RouterProvider router={router}/>
                    </AuthProvider>
                </ConfigProvider>
            </ThemeProvider>
        </QueryClientProvider>
    );
};

export default App;
